/**
*
* useConversations
*
*/

import { useEffect, useRef, useState } from 'react';
import { useApi2 } from 'containers/Api2/index.js';
import { useLocation, useParams } from 'react-router-dom';
import { set, get } from 'lodash-es';
import produce from 'immer';
import useAuthorizedRequest from 'containers/Api2/useAuthorizedRequest.js';
import useStats from 'containers/User/UserProvider/useStats.js';
import useApi3 from 'utils/useApi3/index.js';
import { useSearch } from 'utils/urlUtils.js';
import getSubTopics from './getSubTopics.js';
import { CONVERSATION_TYPE_INDIVIDUAL, MIN_SEARCH_LENGTH, PAGESIZE } from './constants.jsx';
import useUpdateTopics from './useUpdateTopics.js';

const INITIAL_TOPICS = { loading: true };

function useConversations() {
  const { inboxId, topicId, subTopicId } = useParams();
  const archive = useLocation().pathname.includes('archive');
  const { getIn, setIn } = useStats();

  const { q } = useSearch();
  const goSearch = q && q.toString().length >= MIN_SEARCH_LENGTH;
  const [topics, setTopics] = useState(INITIAL_TOPICS);
  const inbox = useApi2({ selector: `inboxes/${inboxId}` });
  function setInTopics(path, value) {
    if (!path || path.length === 0) {
      setTopics(value);
      return;
    }
    setTopics(produce((draft) => {
      set(draft, path, typeof value === 'function' ? value(get(draft, path)) : value);
    }));
  }

  const { authFetch } = useAuthorizedRequest();
  const baseUrl = `inboxes/${inboxId}/topics`;
  const queryParts = [
    goSearch && `q=${q}`,
    'offset=0',
    `limit=${PAGESIZE}`,
    archive && 'archived=true',
  ].filter(Boolean);
  const url = `${baseUrl}${goSearch ? '/search' : ''}?${queryParts.join('&')}`;
  const loadTopics = useApi3({ url, skipParseDates: true }, setTopics);

  const totalUnseen = getIn?.('totalUnseen');
  const lastTotalUnseen = useRef(totalUnseen);
  const updateTopics = useUpdateTopics({ baseUrl, topics, setTopics });

  /* Handle new topics */
  useEffect(() => {
    if (totalUnseen > lastTotalUnseen && topics.ready) {
      // Reload topics
      updateTopics();
      lastTotalUnseen.current = totalUnseen;
    }
  }, [totalUnseen, topics.ready]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Set unseencount to 0, adapt
    const path = ['inboxes', inboxId, 'unseen'];
    const current = getIn(path);
    setIn(path, 0);
    setIn('totalUnseen', getIn('totalUnseen') - current);
  }, [inboxId]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentId = subTopicId || topicId;
  const topic = topics[currentId] || { topicId: currentId, loading: Boolean(topicId) };

  useEffect(() => {
    if (!currentId) {
      return;
    }
    if (topics[currentId]) {
      setTopics((prev) => getSubTopics(prev, topics[currentId]));
      return;
    }
    authFetch({ url: `${baseUrl}/${topicId}`, skipParseDates: true })
      .then((newTopic) => setTopics((prev) => getSubTopics(prev, newTopic)));
  }, [currentId]); // eslint-disable-line react-hooks/exhaustive-deps

  function getParentTopic() {
    if (!topic || (topic.topicType) !== CONVERSATION_TYPE_INDIVIDUAL) {
      return undefined;
    }
    if (subTopicId) {
      return topics[topic.parentTopicId];
    }
    if (topic.subTopics && topic.subTopics.length > 0) {
      return topic;
    }
    return undefined;
  }

  const parentTopic = getParentTopic();
  const query = goSearch ? q.toString() : undefined;

  return {
    inbox, inboxId, baseUrl, topics, query, topic, parentTopic, subTopicId, loadTopics, setTopics, topicId, archive, currentId, setInTopics,
  };
}

export default useConversations;
